import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ExternalLink, Quote, Star } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/i18n";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, delay },
});

function Stars({ n, size = 13 }) {
  return (
    <div className="flex gap-0.5" aria-label={`${n}/5`}>
      {Array.from({ length: 5 }).map((_, s) => (
        <Star key={s} size={size} className={s < Math.round(n || 5) ? "fill-brand-yellow text-brand-yellow" : "text-slate-300 dark:text-slate-700"} />
      ))}
    </div>
  );
}

function GoogleMark() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.4h6.5a5.6 5.6 0 0 1-2.4 3.7v3h3.9c2.3-2.1 3.5-5.2 3.5-8.8z" />
      <path fill="#34A853" d="M12 24c3.2 0 6-1.1 8-2.9l-3.9-3a7.2 7.2 0 0 1-10.8-3.8H1.3v3.1A12 12 0 0 0 12 24z" />
      <path fill="#FBBC05" d="M5.3 14.3a7.2 7.2 0 0 1 0-4.6V6.6H1.3a12 12 0 0 0 0 10.8l4-3.1z" />
      <path fill="#EA4335" d="M12 4.8c1.8 0 3.4.6 4.6 1.8l3.4-3.4A12 12 0 0 0 1.3 6.6l4 3.1A7.2 7.2 0 0 1 12 4.8z" />
    </svg>
  );
}

function ReviewCard({ r, i, google }) {
  return (
    <motion.figure
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ delay: i * 0.1, duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`testimonial-${i}`}
      className="relative flex flex-col rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-6 sm:p-8 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
    >
      <div className="flex items-center justify-between">
        <Quote size={22} className="text-brand-red/50" />
        {google && <GoogleMark />}
      </div>
      <div className="mt-4"><Stars n={r.rating} /></div>
      <blockquote className="mt-4 text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed flex-1 line-clamp-[9]">{r.text}</blockquote>
      <figcaption className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-800 flex items-center gap-3">
        {r.photo && <img src={r.photo} alt={`Opinia klienta wirtualnego biura Warszawa — ${r.name}`} loading="lazy" referrerPolicy="no-referrer" className="w-9 h-9 rounded-full object-cover bg-slate-200" />}
        <div className="min-w-0">
          <div className="font-semibold text-sm truncate">
            {r.author_uri ? <a href={r.author_uri} target="_blank" rel="noopener noreferrer" className="hover:text-brand-teal transition-colors">{r.name}</a> : r.name}
          </div>
          {r.role && <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{r.role}</div>}
        </div>
      </figcaption>
    </motion.figure>
  );
}

export default function Testimonials({ items }) {
  const { t } = useT();
  const [g, setG] = useState(null);

  useEffect(() => {
    api.get("/reviews").then((r) => setG(r.data)).catch(() => setG(null));
  }, []);

  const google = g?.configured && g.reviews?.length > 0;
  const list = google
    ? g.reviews.map((r) => ({ name: r.author, role: r.relative, text: r.text, rating: r.rating, photo: r.photo, author_uri: r.author_uri }))
    : items || [];
  if (list.length === 0) return null;
  const cols = list.length >= 4 ? "lg:grid-cols-4" : list.length === 3 ? "lg:grid-cols-3" : "lg:grid-cols-2";

  return (
    <section id="opinie" data-testid="testimonials-section" className="py-16 sm:py-24 lg:py-32 bg-white dark:bg-slate-950 border-y border-slate-200 dark:border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <motion.p {...fade()} className="font-mono text-xs uppercase tracking-[0.25em] text-slate-600 dark:text-slate-400 font-semibold">
          <span aria-hidden="true" className="inline-block w-6 h-[3px] rounded-full bg-brand-teal mr-3 align-middle" />{t.testimonials.overline}
        </motion.p>
        <div className="flex flex-wrap items-end justify-between gap-6 mt-4">
          <motion.h2 {...fade(0.1)} className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl">
            {t.testimonials.heading}
          </motion.h2>
          {google ? (
            <motion.a
              {...fade(0.15)}
              data-testid="google-rating-summary"
              href={g.maps_uri || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 px-5 py-4 hover:border-brand-teal transition-colors"
            >
              <GoogleMark />
              <div>
                <div className="flex items-center gap-2">
                  <span data-testid="google-rating-value" className="font-display font-bold text-2xl leading-none">{g.rating?.toFixed(1)}</span>
                  <Stars n={g.rating} size={15} />
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  {t.testimonials.google_count.replace("{n}", g.count)} · <span className="underline underline-offset-2 group-hover:text-brand-teal">{t.testimonials.google_link}</span>
                </div>
              </div>
              <ExternalLink size={14} className="text-slate-400 group-hover:text-brand-teal transition-colors" />
            </motion.a>
          ) : (
            <p className="text-slate-600 dark:text-slate-400 text-base max-w-md">{t.testimonials.sub}</p>
          )}
        </div>

        <div className={`mt-12 grid grid-cols-1 md:grid-cols-2 ${cols} gap-6`}>
          {list.slice(0, 8).map((r, i) => <ReviewCard key={i} r={r} i={i} google={google} />)}
        </div>
      </div>
    </section>
  );
}
